import { useQuery } from '@tanstack/react-query';

import { queryKeys, STALE_TIMES, GC_TIMES } from 'lib/query';
import { apiStrapi } from 'services/apiService';
import { logger } from 'utils/logger';

interface LogoFormData {
  url: string;
  mime: string;
}

interface StrapiFormat {
  url: string;
  mime: string;
  sizeInBytes?: number;
}

interface StrapiLogoResponse {
  url?: string;
  mime?: string;
  formats?: Record<string, StrapiFormat>;
}

/**
 * Fetch logo form data from Strapi
 * Selects the largest available format for best quality
 */
const fetchLogoForm = async (): Promise<LogoFormData | null> => {
  try {
    const response = await apiStrapi.get<StrapiLogoResponse>('/organizations/onboarding-logo');
    const data = response.data;

    if (!data?.url || !data?.mime) {
      logger.warn('[useDynamicLogoForm] Invalid logo data format');
      return null;
    }

    let url = data.url;
    let mime = data.mime;

    // Get largest format if available for better quality
    if (data.formats && typeof data.formats === 'object') {
      const formats = Object.values(data.formats) as StrapiFormat[];

      const largestFormat = formats.reduce<StrapiFormat | null>((largest, format) => {
        if (!largest || (format.sizeInBytes || 0) > (largest.sizeInBytes || 0)) {
          return format;
        }
        return largest;
      }, null);

      if (largestFormat) {
        url = largestFormat.url;
        mime = largestFormat.mime;
      }
    }

    return { url, mime };
  } catch (error) {
    logger.error('[useDynamicLogoForm] Failed to fetch:', error);
    throw error;
  }
};

/**
 * Hook for fetching dynamic logo form data from Strapi
 *
 * Uses React Query for automatic caching, deduplication, and background refetching.
 * Replaces the previous module-level cache implementation.
 *
 * @returns {Object} Logo data with loading and error states
 * @returns {string | null} mediaUrl - The URL of the logo image
 * @returns {string | null} mediaType - The MIME type of the logo
 * @returns {boolean} isLoading - Whether the data is being fetched
 * @returns {Error | null} error - Any error that occurred during fetch
 * @returns {Function} refresh - Function to manually refetch the data
 *
 * @example
 * const { mediaUrl, mediaType, isLoading } = useDynamicLogoForm();
 * if (mediaUrl) {
 *   return <img src={mediaUrl} type={mediaType} />;
 * }
 */
export const useDynamicLogoForm = () => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.theme.logo(),
    queryFn: fetchLogoForm,
    staleTime: STALE_TIMES.LONG, // 30 minutes - logo rarely changes
    gcTime: GC_TIMES.LONG, // 1 hour
    retry: 2,
  });

  return {
    mediaUrl: data?.url ?? null,
    mediaType: data?.mime ?? null,
    isLoading,
    error: error as Error | null,
    refresh: refetch,
  };
};

export default useDynamicLogoForm;
